// Another device's change to a fragment, as the editor takes it (`ScriveningsSession.applyExternalText`): the UTF-16
// splices Automerge's patches describe between the heads the document had and its heads now, in order — each in the
// text the ones before it left, which is how `applyExternal` applies them. JS Automerge indexes text in UTF-16, so the
// patches' indices are the editor's offsets as they stand. When the patches can't say (a newer epoch replaced the
// document, so the old heads aren't in it; or the text object itself was replaced), the smallest difference between
// what the editor has and the text now does instead.
import * as A from '@automerge/automerge';

export interface Splice {
  from: number;
  to: number;
  insert: string;
}

/** The text's splices from `beforeHeads` to the document now, or null when the patches can't give them. */
export function textSplices(after: A.Doc<unknown>, beforeHeads: A.Heads | null, key = 'text'): Splice[] | null {
  if (!beforeHeads) return null;
  let patches: A.Patch[];
  try {
    patches = A.diff(after, beforeHeads, A.getHeads(after));
  } catch {
    return null; // heads from another history
  }
  const out: Splice[] = [];
  for (const p of patches) {
    if (p.path[0] !== key) continue;
    // The text object put or deleted whole: nothing to splice from.
    if (p.path.length !== 2 || typeof p.path[1] !== 'number') return null;
    const at = p.path[1];
    if (p.action === 'splice') out.push({ from: at, to: at, insert: p.value });
    else if (p.action === 'del') out.push({ from: at, to: at + (p.length ?? 1), insert: '' });
    else if (p.action === 'mark' || p.action === 'unmark') continue; // no marks in the Swift's text, and none change it
    else return null;
  }
  return out;
}

/** The splices applied to a string, in order (what the editor's text becomes). */
export function applySplices(text: string, splices: Splice[]): string {
  let t = text;
  for (const s of splices) t = t.slice(0, s.from) + s.insert + t.slice(s.to);
  return t;
}

/** The smallest replacement taking `a` to `b`, never splitting a surrogate pair (the editor's `minimalChange`). */
export function difference(a: string, b: string): Splice | null {
  if (a === b) return null;
  let prefix = 0;
  const max = Math.min(a.length, b.length);
  while (prefix < max && a.charCodeAt(prefix) === b.charCodeAt(prefix)) prefix++;
  if (prefix > 0 && prefix < a.length && (a.charCodeAt(prefix) & 0xfc00) === 0xdc00) prefix--;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a.charCodeAt(a.length - 1 - suffix) === b.charCodeAt(b.length - 1 - suffix)) suffix++;
  if (suffix > 0 && (a.charCodeAt(a.length - suffix) & 0xfc00) === 0xdc00) suffix--;
  return { from: prefix, to: a.length - suffix, insert: b.slice(prefix, b.length - suffix) };
}

/**
 * What to hand the editor for a remote change to one fragment: the patches' splices when they take what the editor
 * has to the text now, else the one smallest difference (so the editor always ends up with exactly the stored text).
 */
export function remoteSplices(editorText: string, after: A.Doc<unknown>, beforeHeads: A.Heads | null, now: string): Splice[] {
  const splices = textSplices(after, beforeHeads);
  if (splices && applySplices(editorText, splices) === now) return splices;
  const d = difference(editorText, now);
  return d ? [d] : [];
}
